import { isNumber } from 'lodash'

import {
  CatalogAttribute,
  Filter,
  FilterModel,
  SearchStartType,
  SelectedDynamicFilterModel,
  SortByOption,
} from '@marketplace-web/catalog/catalog-data'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'apply_filters'
  | 'cancel_filters'
  | 'clear_filters'
  | 'help_center_search_result'
  | 'select_filters'
  | 'favorite'
  | 'push_up_learn_more'
  | 'catalog_search_results_ranking'
  | 'mv_visibility_banner'

// https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/common/shared/common.avdl#L34
type ContentSource =
  | 'followed_user_items'
  | 'followed_brand_items'
  | 'vinted_picks'
  | 'promo'
  | 'video_items'
  | 'nearby_items'
  | 'marketing_items'
  | 'new_user_catalog_item'
  | 'new_user_popular_item'
  | 'new_user_sold_item'
  | 'catalog'
  | 'favorite_items'
  | 'user_items'
  | 'marketing_page'
  | 'item_group'
  | 'similar_items'
  | 'other_user_items'
  | 'editorial'
  | 'bundle'
  | 'brand_suggestion'
  | 'promoted_items'
  | 'new_upload'
  | 'new_user_upload'
  | 'new_seller_upload'
  | 'recommended_seller_upload'
  | 'promoted_members'
  | 'promoted_closets'
  | 'promoted_closets_preview'
  | 'find_friends'
  | 'user_discovery'
  | 'news_feed'
  | 'item_collection'
  | 'search'
  | 'catalog_promoted_items'
  | 'search_promoted_items'
  | 'recommended_items'
  | 'random_items'
  | 'canonical_items'
  | 'popular_items'
  | 'popular_promoted_items'
  | 'query_recommended_items'
  | 'favorite_promoted_items'
  | 'items_based_on_recent_purchases'
  | 'personalized_promoted_items'
  | 'lister_activation_banner'
  | 'verified_items'
  | 'verified_promoted_items'
  | 'catalog_promoted_closets'
  | 'search_promoted_closets'
  | 'catalog_ad'
  | 'search_ad'
  | 'feed_ad'
  | 'featured_collection'
  | 'gallery_image'
  | 'gallery_ad'
  | 'recommended_category_items'
  | 'galleried_items'
  | 'price_range_brand_items'
  | 'followed_users'
  | 'price_range_category_items'
  | 'seasonal_thumbnails'
  | 'recently_viewed_items'
  | 'complementary_items'
  | 'saved_search_items'
  | 'hvf_items'
  | 'hvf_promoted_items'
  | 'accessories_banner'
  | 'search_by_image'
  | 'hvf_banner'
  | 'ivs_banner'
  | 'homepage_block'
  | 'dac7_banner'
  | 'spring_cleaning_banner'
  | 'edit_price'
  | 'designer_recommended_items'
  | 'designer_items_based_on_recent_purchases'
  | 'designer_shop_by_brand'
  | 'designer_new_item_upload'
  | 'designer_ivs_banner'
  | 'electronics_new_item_upload'
  | 'electronics_items_by_categories'
  | 'electronics_recommended_items'
  | 'electronics_recommended_brand_items'
  | 'electronics_suggested_categories'
  | 'electronics_video_games_and_consoles_banner'
  | 'electronics_mobile_phones_and_tablets_banner'
  | 'video_games_and_consoles_banner'
  | 'mobile_phones_and_tablets_banner'
  | 'more_similar_items'
  | 'electronics_general_banner'
  | 'electronics_highlights_banner'
  | 'electronics_phones_and_tablets_banner'
  | 'electronics_laptops_banner'
  | 'collectibles_banner'
  | 'high_value_electronics_banner'
  | 'promoted_category_items'
  | 'feed_women_recommended_items'
  | 'feed_men_recommended_items'
  | 'feed_kids_recommended_items'
  | 'feed_entertainment_recommended_items'
  | 'feed_home_recommended_items'
  | 'feed_pet_care_recommended_items'
  | 'feed_sports_recommended_items'
  | 'feed_hobbies_collectables_recommended_items'
  | 'feed_women_new_items'
  | 'feed_men_new_items'
  | 'feed_kids_new_items'
  | 'feed_entertainment_new_items'
  | 'feed_home_new_items'
  | 'feed_pet_care_new_items'
  | 'feed_sports_new_items'
  | 'feed_hobbies_collectables_new_items'

// Source: https://github.com/vinted/dwh-schema-registry/blob/e39aec4da4a00a8e70a8839adfbc164d04d10b91/avro/events/common/shared/common.avdl#L34
type ContentTypes = 'item' | 'ad' | 'lister_activation_banner'

type endOfListEventArgs = {
  itemCount: number
  page?: number
  perPage?: number
}

type EndOfListEventExtra = {
  item_count: number
  page?: number
  per_page?: number
}

type TrackingEvent = {
  event: string
  extra?: object
  userId?: number
}

export const endOfListEvent = (args: endOfListEventArgs): TrackingEvent => {
  const { itemCount, page, perPage } = args

  const extra: EndOfListEventExtra = {
    item_count: itemCount,
  }

  if (page) extra.page = page
  if (perPage) extra.per_page = perPage

  return {
    event: 'user.reach_end_of_list',
    extra,
  }
}

const nullifyEmptyValue = <T>(value: T): T | null =>
  !value ||
  (Array.isArray(value) && (value as Array<unknown>).length === 0) ||
  Object.keys(value).length === 0
    ? null
    : value

type UserFilterItemsEventArgs = {
  itemCount: number
  filters: FilterModel
  searchQuery: string | null
  searchCorrelationId: string
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchStartId: string | null
  searchStartType: string | null
  selectedDynamicFilters: Array<SelectedDynamicFilterModel>
  searchByImageUuid: string | null
}

type UserFilterItemsEventExtra = {
  item_count: number
  search_correlation_id: string
  search_session_id: string
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  conditions: Partial<{
    search_term: string | null
    brand_ids: Array<number> | null
    catalog_ids: Array<number> | null
    color_ids: Array<number> | null
    material_ids: Array<number> | null
    video_game_rating_ids: Array<number> | null
    size_ids: Array<number> | null
    status_ids: Array<number> | null
    price_from: number | null
    price_to: number | null
    order: string | null
    popular: boolean
    search_by_image_uuid: string | null
  }>
  search_start_id?: string
  search_start_type?: string
  selected_filters?: Record<string, Array<number>>
}

export const userFilterItemsEvent = (args: UserFilterItemsEventArgs): TrackingEvent => {
  const {
    itemCount,
    filters,
    searchQuery,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchStartId,
    searchStartType,
    selectedDynamicFilters,
    searchByImageUuid,
  } = args

  const extra: UserFilterItemsEventExtra = {
    item_count: itemCount,
    search_correlation_id: searchCorrelationId,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    conditions: {
      search_term: searchQuery,
      catalog_ids: nullifyEmptyValue(filters.catalogIds),
      price_from: filters.priceFrom ? parseFloat(filters.priceFrom) : null,
      price_to: filters.priceTo ? parseFloat(filters.priceTo) : null,
      order: filters.sortBy ? nullifyEmptyValue(filters.sortBy) : SortByOption.Relevance,
      popular: filters.isPopularCatalog,
      search_by_image_uuid: searchByImageUuid,
    },
  }

  if (searchStartType) {
    extra.search_start_type = searchStartType
  }

  if (searchStartId) {
    extra.search_start_id = searchStartId
  }

  if (searchByImageUuid) {
    extra.search_start_type = SearchStartType.SearchByImage
  }

  selectedDynamicFilters
    .filter(({ ids }) => ids.length)
    .forEach(({ type, ids }) => {
      if (!extra.selected_filters) extra.selected_filters = {}

      extra.selected_filters[type] = ids
      // TODO: remove this when dynamic catalog filters scaled
      extra.conditions[`${type}_ids`] = ids
    })

  return {
    event: 'user.filter_items',
    extra,
  }
}

type UserPaginateCatalogEventArgs = {
  currentPage: number
  searchCorrelationId: string
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
}

type UserPaginateCatalogEventExtra = {
  page: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
}

export const userPaginateCatalogEvent = (args: UserPaginateCatalogEventArgs): TrackingEvent => {
  const {
    currentPage,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
  } = args

  const extra: UserPaginateCatalogEventExtra = {
    page: currentPage,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId

  return {
    event: 'user.paginate_catalog',
    extra,
  }
}

type UserAppliedOrderEventArgs = {
  order: SortByOption
  searchSessionId: string
  globalSearchSessionId: string | null
}

type UserAppliedOrderEventExtra = {
  order: string
  search_session_id?: string
  global_search_session_id?: string
}

export const userAppliedOrderEvent = (args: UserAppliedOrderEventArgs) => {
  const { order, searchSessionId, globalSearchSessionId } = args

  const extra: UserAppliedOrderEventExtra = {
    order,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId

  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId

  return { event: 'user.applied_order', extra }
}

type ClickCatalogBackArgs = {
  searchSessionId: string | null
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId: string | null
}

type ClickCatalogBackExtra = {
  search_session_id: string | null
  global_search_session_id: string | null
  global_catalog_browse_session_id: string | null
}
export const clickCatalogBack = (args: ClickCatalogBackArgs) => {
  const extra: ClickCatalogBackExtra = {
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId,
    global_catalog_browse_session_id: args.globalCatalogBrowseSessionId,
  }

  return {
    event: 'user.click_catalog_back',
    extra,
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type FavouriteItemEventArgs = {
  itemId: number
  isFollowEvent: boolean
  contentSource?: ContentSource | null
  searchCorrelationId?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  itemOwnerId?: string | number | null
}

type FavouriteItemEventExtra = {
  item_id: number
  content_source?: ContentSource
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string
  item_owner_id?: string | number
}

export const favouriteItemEvent = ({
  itemId,
  isFollowEvent,
  contentSource,
  searchCorrelationId,
  searchSessionId,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  homepageSessionId,
  itemOwnerId,
}: FavouriteItemEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item'
  const extra: FavouriteItemEventExtra = { item_id: itemId }

  if (contentSource) extra.content_source = contentSource
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (itemOwnerId) extra.item_owner_id = itemOwnerId

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ContentTypes
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: string
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ContentTypes
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: string
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget = 'mv_visibility_banner'

type ViewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const viewEvent = (args: ViewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type ClickFilterEventArgs = {
  filter: Filter
  screen?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  type?: string
  code?: string
}

type ClickFilterEventExtra = {
  filter: Filter
  screen?: string
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
  type?: string
  code?: string
}

export const clickFilterEvent = (args: ClickFilterEventArgs): TrackingEvent => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    type,
    code,
  } = args
  const extra: ClickFilterEventExtra = {
    filter,
    screen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (type) extra.type = type
  if (code) extra.code = code

  return {
    event: 'user.click_filter',
    extra,
  }
}

type SelectCatalogEventArgs = {
  catalogIds: Array<number>
  attributeId?: CatalogAttribute
  screen?: string
}

type SelectCatalogEventExtra = {
  catalog_id: string
  attribute_id: number | null
  screen?: string
}

export const selectCatalogEvent = (args: SelectCatalogEventArgs): TrackingEvent => {
  const { catalogIds, attributeId, screen } = args

  const extra: SelectCatalogEventExtra = {
    catalog_id: catalogIds.join(','),
    attribute_id: attributeId ?? null,
  }

  if (screen) extra.screen = screen

  return {
    event: 'user.select_catalog',
    extra,
  }
}
